/* eslint-disable class-methods-use-this */
import get from 'lodash/get';
import isUndefined from 'lodash/isUndefined';
import isFunction from 'lodash/isFunction';
import { interactionSubscriptionVars } from '@AppRoutes/constants';

export const BROWSER_INTERACTIONS_NAMES = {
  TIME_TO_FIND_SUBSTANCES: 'timeToFindSubstances',
  TIME_TO_VIEW_SUBSTANCES: 'timeToViewSubstances',
  TIME_TO_APPLY_FILTERS_TO_SUBSTANCES: 'timeToApplyFiltersToSubstances',
};

class NewRelicService {
  _distributedTracingConflictResolved = false;

  _browserInteractions = {};

  constructor() {
    this.isDisabled = 'false';

    this._init();
  }

  /**
   * @ngdoc method
   * @name _init
   * @methodOf RxNewRelic.NewRelicService
   */
  _init() {
    this._backupFunctions();

    if (!this._distributedTracingConflictResolved) {
      this._initDistributedTracingHeadersOverrideToPreventConflictsWithReaxysImplementation();
    }
  }

  /**
   * @ngdoc method
   * @name setNoTracking
   * @methodOf  RxNewRelic.NewRelicService
   * @param {Boolean | 'true' | any} noTracking
   */
  setNoTracking(noTracking) {
    if (!isUndefined(noTracking)) {
      const stringNoTracking = noTracking.toString().toLowerCase();
      if (stringNoTracking !== this.isDisabled) {
        if (stringNoTracking === 'true') {
          this._disableTracking();
        } else {
          this._enableTracking();
        }
        this.isDisabled = stringNoTracking;
      }
    }
  }

  /**
   * @ngdoc method
   * @name _backupFunctions
   * @methodOf  RxNewRelic.NewRelicService
   * @description save original values
   */
  _backupFunctions() {
    this.info = get(window, 'NREUM.info', {});
    this.originalXhrFunction = get(window, 'NREUM.o.XHR.prototype.send', null);
    this.originalSendBeaconFunction = get(window, 'navigator.sendBeacon', null);
  }

  /*
   * Problem description:
   * When distributed tracing is enabled in NewRelic, it generates its own traceparent and tracestate headers.
   * And it conflicts with the Reaxys implementation, which also generates trace-headers.
   * The aim is to resolve conflict and make NR work with Reaxys implementation of trace-headers
   * */
  _initDistributedTracingHeadersOverrideToPreventConflictsWithReaxysImplementation() {
    const originalSetRequestHeader = XMLHttpRequest.prototype.setRequestHeader;

    XMLHttpRequest.prototype.setRequestHeader = function setRequestHeader(header, value) {
      if (header === 'traceparent') {
        // eslint-disable-next-line no-unused-vars,sonarjs/no-unused-vars,sonarjs/no-dead-store
        const [version, traceId, parentId, flags] = value.split('-');

        // Prevent NR from settings its own traceparent header.
        // Currently, check relies only on trace-flags (NR sets 01, Reaxys implementation sets 00)
        if (flags === '01') {
          return;
        }

        // Update distributed tracing variables in NR with Reaxys traceparent header
        if (this['nr@context'] && this['nr@context'].dt) {
          this['nr@context'].dt.traceContextParentHeader = value;
          this['nr@context'].dt.traceId = traceId;
          this['nr@context'].dt.spanId = parentId;
        }
      }

      if (header === 'tracestate') {
        const traceStates = value.split(',');

        const nrTraceState = traceStates.find((traceState) => {
          // eslint-disable-next-line no-unused-vars,sonarjs/no-unused-vars,sonarjs/no-dead-store
          const [traceStateKey, traceStateValue] = traceState.split('=');
          return traceStateKey && traceStateKey.indexOf('@nr') > -1;
        });

        // Prevent NR from settings its own tracestate header.
        // Currently, check relies on trace state key matching "@nr"
        if (nrTraceState) {
          return;
        }

        // Update distributed tracing variables in NR with Reaxys tracestate header
        if (this['nr@context'] && this['nr@context'].dt) {
          this['nr@context'].dt.traceContextStateHeader = value;
        }
      }

      originalSetRequestHeader.call(this, header, value);
    };

    this._distributedTracingConflictResolved = true;
  }

  /**
   * @ngdoc method
   * @name _disableTracking
   * @methodOf  RxNewRelic.NewRelicService
   * @description update native function with interceptor
   */
  _disableTracking() {
    if (isFunction(this.originalXhrFunction)) {
      const instance = this;

      // eslint-disable-next-line func-names
      window.NREUM.o.XHR.prototype.send = function (...args) {
        const dest = get(this, 'nr@context.params.host', '').split(':')[0];
        if (instance._isUrlDeprecated(dest)) {
          return;
        }

        // eslint-disable-next-line consistent-return
        return instance.originalXhrFunction.apply(this, args);
      };
    }

    if (isFunction(this.originalSendBeaconFunction)) {
      const instance = this;

      // eslint-disable-next-line func-names
      window.navigator.sendBeacon = function (...args) {
        if (instance._isUrlDeprecated(args[0])) {
          return;
        }

        // eslint-disable-next-line consistent-return
        return instance.originalSendBeaconFunction.apply(this, args);
      };
    }
  }

  /**
   * @ngdoc method
   * @name _enableTracking
   * @methodOf  RxNewRelic.NewRelicService
   * @description restore original values
   */
  _enableTracking() {
    if (isFunction(this.originalXhrFunction)) {
      window.NREUM.o.XHR.prototype.send = this.originalXhrFunction;
    }

    if (isFunction(this.originalSendBeaconFunction)) {
      window.navigator.sendBeacon = this.originalSendBeaconFunction;
    }
  }

  /**
   * @ngdoc method
   * @name _isUrlDeprecated
   * @methodOf  RxNewRelic.NewRelicService
   * @description check if url deprecated
   * @param url
   */
  _isUrlDeprecated(url) {
    if (!isUndefined(url)) {
      const stringUrl = url.toString();
      return stringUrl.indexOf(this.info.beacon) > -1 || stringUrl.indexOf(this.info.errorBeacon) > -1;
    }

    return false;
  }

  /**
   * @ngdoc method
   * @name noticeError
   * @methodOf  RxNewRelic.NewRelicService
   * @description report error
   * @param error
   * @param customAttributes
   */
  noticeError = (error, customAttributes) => {
    const noticeError = get(window, 'newrelic.noticeError');
    if (noticeError) noticeError(error, customAttributes);
  };

  _getBrowserInteraction = (name) => {
    if (name === BROWSER_INTERACTIONS_NAMES.TIME_TO_FIND_SUBSTANCES) {
      return {
        shouldCancelInteraction: (state) => state.name !== 'main.search.quick.results',
      };
    }

    if (name === BROWSER_INTERACTIONS_NAMES.TIME_TO_VIEW_SUBSTANCES) {
      return {
        shouldCancelInteraction: (state, toParams, ownParams) => {
          const context = get(toParams, 'context');
          const searchId = get(toParams, 'searchId');
          const ownSearchId = get(ownParams, 'searchId');
          return state.name !== 'main.results.context' || context !== 'substances' || searchId !== ownSearchId;
        },
      };
    }

    if (name === BROWSER_INTERACTIONS_NAMES.TIME_TO_APPLY_FILTERS_TO_SUBSTANCES) {
      return {
        shouldCancelInteraction: (state, toParams, ownParams) => {
          const context = get(toParams, 'context');
          const hitSetKey = get(toParams, 'hitSetKey');
          const ownHitSetKey = get(ownParams, 'hitSetKey');
          return state.name !== 'main.results.context' || context !== 'substances' || ownHitSetKey !== hitSetKey;
        },
      };
    }
    return null;
  };

  createBrowserInteraction = (name, params) => {
    const browserInteraction = this._getBrowserInteraction(name);
    if (!browserInteraction) {
      return;
    }

    const startTime = Date.now();
    interactionSubscriptionVars.subscription = false;

    const cancelBrowserInteraction = () => {
      interactionSubscriptionVars.subscription = false;
      this._browserInteractions[name] = null;
    };
    interactionSubscriptionVars.cancelBrowserInteraction = cancelBrowserInteraction;

    interactionSubscriptionVars.interaction = {
      ...browserInteraction,
      params,
      update: (updatedParams) => {
        interactionSubscriptionVars.interaction.params = updatedParams;
      },
      end: () => {
        cancelBrowserInteraction();
        const NRInteraction = get(window, 'newrelic.interaction');
        NRInteraction()
          .setName(name)
          .setAttribute(name, Date.now() - startTime)
          .save()
          .end();
      },
    };

    interactionSubscriptionVars.subscription = true;
    this._browserInteractions[name] = interactionSubscriptionVars.interaction;
    const { interaction } = interactionSubscriptionVars;
    // eslint-disable-next-line consistent-return
    return interaction;
  };

  endBrowserInteraction = (name) => {
    if (!name) {
      return;
    }
    if (!this._browserInteractions[name]) {
      return;
    }
    this._browserInteractions[name].end();
  };

  trackRouteChanges = (url) => {
    const NRInteraction = get(window, 'newrelic.interaction');
    NRInteraction().setName(url).save().end();
  };
}

export const NewRelicServiceClass = NewRelicService;

export default new NewRelicService();
